import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import WbCloudyIcon from '@mui/icons-material/WbCloudy';
import CloudIcon from '@mui/icons-material/Cloud';
import DehazeIcon from '@mui/icons-material/Dehaze';
import GrainIcon from '@mui/icons-material/Grain';
import UmbrellaIcon from '@mui/icons-material/Umbrella';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ThunderstormIcon from '@mui/icons-material/Thunderstorm';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import {
    ClockWidgetConfig,
    CricketMatchSnapshot,
    CricketScoreWidgetConfig,
    CricketWidgetPayload,
    NewsTickerWidgetConfig,
    OnDemandPlayerWidgetConfig,
    OnDemandQueueEntry,
    OnDemandQrPosition,
    ReelPlayerWidgetConfig,
    StockQuote,
    StockTickerWidgetConfig,
    WeatherNow,
    WeatherWidgetConfig,
    clockLines,
    formatFixtureStamp,
    onDemandBannerLabel,
    onDemandPercent,
    onDemandStripLabel,
} from './composer.types';
import QRCode from 'qrcode';

/*
 * Pure widget renderers shared by the portal Composer tab and the offline screen player.
 * Sizes are in screen pixels; the parent scales the whole layout.
 */

const WEATHER_ICONS: Record<string, React.ElementType> = {
    'clear-day': WbSunnyIcon,
    'clear-night': NightsStayIcon,
    'partly-cloudy': WbCloudyIcon,
    overcast: CloudIcon,
    fog: DehazeIcon,
    drizzle: GrainIcon,
    rain: UmbrellaIcon,
    snow: AcUnitIcon,
    thunderstorm: ThunderstormIcon,
};

export const WeatherWidgetView: React.FC<{ config: WeatherWidgetConfig; weather: WeatherNow | null; fallbackLocation?: string }> = ({
    config,
    weather,
    fallbackLocation = '',
}) => {
    const Icon = WEATHER_ICONS[weather?.icon || ''] || CloudIcon;
    const location = config.labelOverride || weather?.location || fallbackLocation || 'Location';
    const temp = weather && Number.isFinite(weather.tempC) ? `${Math.round(weather.tempC)}°C` : '--°C';
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: `${Math.round(config.fontSize * 0.3)}px`,
                px: `${Math.round(config.fontSize * 0.3)}px`,
                backgroundColor: config.backgroundColor,
                color: config.color,
                fontFamily: config.fontFamily,
            }}
        >
            {config.showIcon && <Icon titleAccess={weather?.label || 'Weather'} sx={{ fontSize: Math.round(config.fontSize * 1.4), color: 'inherit', flex: '0 0 auto' }} />}
            <Box sx={{ minWidth: 0 }}>
                <Box sx={{ fontSize: config.fontSize, fontWeight: 700, lineHeight: 1.05, whiteSpace: 'nowrap' }}>{temp}</Box>
                <Box sx={{ fontSize: Math.max(8, Math.round(config.fontSize * 0.42)), lineHeight: 1.2, opacity: 0.9, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {location}
                </Box>
            </Box>
        </Box>
    );
};

export interface ScrollingTickerProps {
    /** One pass of the ticker. It is repeated so the loop has no gap. */
    children: React.ReactNode;
    separator?: React.ReactNode;
    speedPxPerSec: number;
    animate?: boolean;
    signature: string;
    backgroundColor: string;
    color: string;
    fontFamily: string;
    fontSize: number;
}

/** Right-to-left bar. Measures one copy and scrolls by exactly that width. */
export const ScrollingTicker: React.FC<ScrollingTickerProps> = ({
    children,
    separator,
    speedPxPerSec,
    animate = true,
    signature,
    backgroundColor,
    color,
    fontFamily,
    fontSize,
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const copyRef = useRef<HTMLSpanElement | null>(null);
    const trackRef = useRef<HTMLDivElement | null>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [copyWidth, setCopyWidth] = useState(0);

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;
        const update = () => {
            setContainerWidth(el.offsetWidth);
            setCopyWidth(copyRef.current?.offsetWidth || 0);
        };
        update();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(update);
        ro.observe(el);
        if (copyRef.current) ro.observe(copyRef.current);
        return () => ro.disconnect();
    }, [signature]);

    const copies = copyWidth > 0 ? Math.max(2, Math.ceil(containerWidth / copyWidth) + 1) : 2;

    useEffect(() => {
        const track = trackRef.current;
        if (!track || !animate || copyWidth <= 0 || typeof track.animate !== 'function') return undefined;
        const speed = Math.max(10, speedPxPerSec || 120);
        const animation = track.animate([{ transform: 'translateX(0px)' }, { transform: `translateX(-${copyWidth}px)` }], {
            duration: (copyWidth / speed) * 1000,
            iterations: Infinity,
            easing: 'linear',
        });
        return () => animation.cancel();
    }, [copyWidth, speedPxPerSec, animate, signature]);

    return (
        <Box
            ref={containerRef}
            sx={{ width: '100%', height: '100%', overflow: 'hidden', display: 'flex', alignItems: 'center', backgroundColor, color, fontFamily, fontSize, lineHeight: 1.2 }}
        >
            <Box ref={trackRef} sx={{ display: 'flex', flexWrap: 'nowrap', willChange: 'transform' }}>
                {Array.from({ length: copies }, (_, index) => (
                    <Box component="span" key={index} ref={index === 0 ? copyRef : undefined} sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'pre', flex: '0 0 auto' }}>
                        {children}
                        {separator}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

/** Right-to-left news ticker. The text is repeated so the loop has no gap. */
export const NewsTickerView: React.FC<{ config: NewsTickerWidgetConfig; items: string[]; placeholder?: string; animate?: boolean }> = ({
    config,
    items,
    placeholder = 'News headlines appear here',
    animate = true,
}) => {
    const lines = items.length ? items : [placeholder];
    return (
        <ScrollingTicker
            speedPxPerSec={config.speedPxPerSec}
            animate={animate}
            signature={`${lines.join('\u0001')}|${config.separator}|${config.fontFamily}|${config.fontSize}`}
            backgroundColor={config.backgroundColor}
            color={config.color}
            fontFamily={config.fontFamily}
            fontSize={config.fontSize}
        >
            {lines.map((line, i) => (
                <React.Fragment key={i}>
                    <span>{line}</span>
                    <span>{config.separator}</span>
                </React.Fragment>
            ))}
        </ScrollingTicker>
    );
};

const priceText = (quote: StockQuote) => {
    const digits = Math.abs(quote.price) >= 1000 ? 2 : Math.abs(quote.price) >= 1 ? 2 : 4;
    return quote.price.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: digits });
};

/** One quote as shown in the stock ticker, with the change coloured up or down. */
export const StockQuoteItem: React.FC<{ quote: StockQuote; config: StockTickerWidgetConfig }> = ({ quote, config }) => {
    const up = quote.change > 0;
    const flat = quote.change === 0;
    const arrow = flat ? '•' : up ? '▲' : '▼';
    const label = config.labelField === 'symbol' ? quote.symbol : config.labelField === 'name' ? quote.name : `${quote.name} (${quote.symbol})`;
    const change = `${quote.change > 0 ? '+' : ''}${quote.change.toFixed(2)}`;
    const percent = `${quote.changePct > 0 ? '+' : ''}${quote.changePct.toFixed(2)}%`;
    return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: '0.45em' }}>
            <span>{label}</span>
            <span style={{ fontWeight: 700 }}>{priceText(quote)}</span>
            {config.showCurrency && quote.currency && <span>{quote.currency}</span>}
            {(config.showTrend || config.showChange || config.showChangePercent) && (
                <span style={{ color: flat ? 'inherit' : up ? config.upColor : config.downColor }}>
                    {config.showTrend ? `${arrow} ` : ''}
                    {config.showChange ? change : ''}
                    {config.showChange && config.showChangePercent ? ' ' : ''}
                    {config.showChangePercent ? `(${percent})` : ''}
                </span>
            )}
        </Box>
    );
};

export const StockTickerView: React.FC<{ config: StockTickerWidgetConfig; quotes: StockQuote[]; placeholder?: string; animate?: boolean; stale?: boolean }> = ({
    config,
    quotes,
    placeholder = 'Stock prices appear here',
    animate = true,
    stale = false,
}) => (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        <ScrollingTicker
            speedPxPerSec={config.speedPxPerSec}
            animate={animate}
            signature={`${quotes.map((q) => `${q.symbol}:${q.price}:${q.change}`).join('|')}|${config.labelField}|${config.fontSize}|${stale ? 1 : 0}`}
            backgroundColor={config.backgroundColor}
            color={config.color}
            fontFamily={config.fontFamily}
            fontSize={config.fontSize}
        >
            {quotes.length ? (
                quotes.map((quote) => (
                    <React.Fragment key={quote.symbol}>
                        <StockQuoteItem quote={quote} config={config} />
                        <span>{config.separator}</span>
                    </React.Fragment>
                ))
            ) : (
                <span>{placeholder}</span>
            )}
        </ScrollingTicker>
        {stale && (
            <Box sx={{ position: 'absolute', top: 2, right: 8, fontSize: Math.max(12, Math.round(config.fontSize * 0.4)), letterSpacing: '0.08em', opacity: 0.9 }}>
                STALE
            </Box>
        )}
    </Box>
);

const sideLine = (side?: { short?: string; name?: string; runs: number | null; wickets: number | null; overs: string }) =>
    side && side.runs != null ? `${side.short || side.name} ${side.runs}/${side.wickets ?? 0} (${side.overs})` : side?.short || side?.name || '';

export const CricketScoreView: React.FC<{ config: CricketScoreWidgetConfig; payload?: CricketWidgetPayload | null; placeholder?: boolean; timezone?: string }> = ({
    config,
    payload,
    placeholder = false,
    timezone,
}) => {
    const [index, setIndex] = useState(0);
    const matches = payload?.matches || [];
    const zone = config.timezoneOverride || timezone || undefined;
    const fixtureLabel = (match: CricketMatchSnapshot) => {
        if (match.state !== 'fixture') return match.status;
        const stamp = match.startsAt ? formatFixtureStamp(match.startsAt, zone) : '';
        return stamp ? `Next: ${stamp}` : match.status;
    };
    useEffect(() => {
        if (matches.length < 2 || config.style === 'ticker') return undefined;
        const timer = setInterval(() => setIndex((i) => (i + 1) % matches.length), Math.max(5, config.rotateSec) * 1000);
        return () => clearInterval(timer);
    }, [matches.length, config.rotateSec, config.style]);
    const match = matches[index % Math.max(matches.length, 1)];
    const hide = !match && !(config.showNextMatch && payload?.next);
    if (hide) {
        return placeholder ? (
            <Box sx={{ width: '100%', height: '100%', border: '1px dashed rgba(255,255,255,0.35)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                No live match. Hidden on screen.
            </Box>
        ) : null;
    }
    const shown = match || payload?.next;
    if (!shown) return null;
    const scoreOnly = config.style !== 'compact' && config.style !== 'ticker' && shown.detail === 'score' && shown.state !== 'fixture';
    const line = shown.teams.map((side) => sideLine(side)).filter(Boolean).join('  vs  ');
    if (config.style === 'ticker' && matches.length) {
        return (
            <ScrollingTicker speedPxPerSec={80} signature={matches.map((m) => m.matchId + m.status + m.startsAt).join('|')} backgroundColor={config.backgroundColor} color={config.color} fontFamily={config.fontFamily} fontSize={config.fontSize}>
                {matches.map((m) => (
                    <span key={m.matchId}>{m.teams.map((s) => sideLine(s)).join(' vs ')} {fixtureLabel(m)}</span>
                ))}
            </ScrollingTicker>
        );
    }
    return (
        <Box sx={{ width: '100%', height: '100%', overflow: 'hidden', boxSizing: 'border-box', p: 1, backgroundColor: config.backgroundColor, color: config.color, fontFamily: config.fontFamily, fontSize: config.fontSize, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5 }}>
            <Box sx={{ fontWeight: 700 }}>{line || shown.name}</Box>
            <Box sx={{ fontSize: '0.72em', opacity: 0.9 }}>{fixtureLabel(shown)}</Box>
            {(config.style === 'broadcast' || config.style === 'ball-by-ball' || config.style === 'scorecard') && shown.batting?.length > 0 && (
                <Box sx={{ fontSize: '0.75em' }}>{shown.batting.map((b) => `${b.name} ${b.runs} (${b.balls})`).join('  ·  ')}{shown.bowling ? `   ${shown.bowling.name} ${shown.bowling.wickets}-${shown.bowling.runs}` : ''}</Box>
            )}
            {config.style === 'ball-by-ball' && shown.recentBalls?.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, fontSize: '0.7em' }}>
                    {shown.recentBalls.slice(-8).map((ball, i) => (
                        <Box key={i} sx={{ px: 0.6, borderRadius: 1, background: ball.wicket ? '#c62828' : config.accentColor, color: '#111' }}>{ball.extra || ball.text || ball.runs}</Box>
                    ))}
                </Box>
            )}
            {scoreOnly && <Box sx={{ fontSize: '0.65em', opacity: 0.75 }}>Score only</Box>}
        </Box>
    );
};

export const ClockWidgetView: React.FC<{ config: ClockWidgetConfig; timezone?: string }> = ({ config, timezone }) => {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        const tick = () => {
            setNow(new Date());
            timer = setTimeout(tick, 1000 - (Date.now() % 1000) + 5);
        };
        timer = setTimeout(tick, 1000 - (Date.now() % 1000) + 5);
        return () => clearTimeout(timer);
    }, []);
    const zone = config.timezoneOverride || timezone || undefined;
    const lines = clockLines(now, config.format, zone, Boolean(config.showDate));
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: `${Math.round(config.fontSize * 0.25)}px`,
                backgroundColor: config.backgroundColor,
                color: config.color,
                fontFamily: config.fontFamily,
                fontVariantNumeric: 'tabular-nums',
            }}
        >
            {lines.map((line, i) => (
                <Box key={i} sx={{ fontSize: i === 0 ? config.fontSize : Math.max(8, Math.round(config.fontSize * 0.42)), fontWeight: i === 0 ? 700 : 400, lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                    {line}
                </Box>
            ))}
        </Box>
    );
};

/** On the screen the playlist plays here; in the portal it is a labelled placeholder. */
export const ReelPlayerWidgetView: React.FC<{ config: ReelPlayerWidgetConfig; children?: React.ReactNode; label?: string }> = ({ config, children, label }) => (
    <Box sx={{ width: '100%', height: '100%', overflow: 'hidden', position: 'relative', backgroundColor: config.backgroundColor }}>
        {children ?? (
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    color: 'rgba(255,255,255,0.75)',
                    border: '2px dashed rgba(255,255,255,0.35)',
                    textAlign: 'center',
                    px: 2,
                }}
            >
                <SmartDisplayIcon sx={{ fontSize: 96 }} />
                <Box sx={{ fontSize: 36, fontWeight: 600 }}>Reel Player</Box>
                <Box sx={{ fontSize: 26, opacity: 0.85 }}>{label || 'Plays the screen playlist'}</Box>
            </Box>
        )}
    </Box>
);

const QR_ALIGN: Record<OnDemandQrPosition, { alignItems: string; justifyContent: string }> = {
    'top-left': { alignItems: 'flex-start', justifyContent: 'flex-start' },
    'top-right': { alignItems: 'flex-end', justifyContent: 'flex-start' },
    'bottom-left': { alignItems: 'flex-start', justifyContent: 'flex-end' },
    'bottom-right': { alignItems: 'flex-end', justifyContent: 'flex-end' },
    center: { alignItems: 'center', justifyContent: 'center' },
};

const QrCard: React.FC<{ url: string; caption: string }> = ({ url, caption }) => {
    const [src, setSrc] = useState('');
    useEffect(() => {
        let cancelled = false;
        QRCode.toDataURL(url, { margin: 1, width: 480, color: { dark: '#111111', light: '#ffffff' } })
            .then((data) => {
                if (!cancelled) setSrc(data);
            })
            .catch(() => {
                if (!cancelled) setSrc('');
            });
        return () => {
            cancelled = true;
        };
    }, [url]);
    return (
        <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: '#fff', color: '#111', textAlign: 'center', width: '100%' }}>
            {src ? <Box component="img" src={src} alt="On Demand Player" sx={{ width: '100%', height: 'auto', display: 'block' }} /> : null}
            {caption && <Box sx={{ mt: 1, fontSize: 28, fontWeight: 600, lineHeight: 1.2 }}>{caption}</Box>}
        </Box>
    );
};

const QueueStrip: React.FC<{ queueCount: number; next: OnDemandQueueEntry | null }> = ({ queueCount, next }) => {
    const label = onDemandStripLabel(queueCount, next);
    if (!label) return null;
    return (
        <Box sx={{ position: 'absolute', top: 12, left: 12, right: 12, zIndex: 5, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
            <Box sx={{ px: 2, py: 0.75, borderRadius: 10, backgroundColor: 'rgba(0,0,0,0.72)', color: '#fff', fontSize: 22, fontWeight: 600, maxWidth: '100%' }}>
                {label}
            </Box>
        </Box>
    );
};

/**
 * Full-screen On Demand Player. Idle shows a QR code or nothing. Playing fills the widget.
 * The queue strip sits on the widget; the download banner is drawn by the screen player above the layout.
 */
export const OnDemandPlayerView: React.FC<{
    config: OnDemandPlayerWidgetConfig;
    qrUrl?: string;
    playing?: boolean;
    queueCount?: number;
    next?: OnDemandQueueEntry | null;
    children?: React.ReactNode;
}> = ({ config, qrUrl, playing, queueCount = 0, next = null, children }) => {
    if (!playing && config.idleDisplay === 'none') return null;
    const align = QR_ALIGN[config.qrPosition] || QR_ALIGN['bottom-right'];
    return (
        <Box sx={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', backgroundColor: playing ? config.backgroundColor : 'transparent' }}>
            {playing ? children : qrUrl ? (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', p: 3, ...align }}>
                    <Box sx={{ width: `${Math.round(config.qrSize * 100)}%`, maxWidth: '70%' }}>
                        <QrCard url={qrUrl} caption={config.caption} />
                    </Box>
                </Box>
            ) : null}
            {(playing || config.idleDisplay === 'qr') && <QueueStrip queueCount={queueCount} next={next} />}
        </Box>
    );
};

/** Full-width notice above every widget while the next On Demand playlist is downloading. */
export const OnDemandDownloadBanner: React.FC<{ playlists: OnDemandQueueEntry[] }> = ({ playlists }) => {
    const live = playlists.find((entry) => entry.state === 'downloading' || entry.state === 'error') || null;
    const [held, setHeld] = useState<OnDemandQueueEntry | null>(null);
    const previous = useRef<string>('');
    useEffect(() => {
        const signature = playlists.map((entry) => `${entry.id}:${entry.state}`).join('|');
        const was = previous.current;
        previous.current = signature;
        if (live) {
            setHeld(null);
            return undefined;
        }
        const finished = playlists.find((entry) => entry.state === 'ready' && was.includes(`${entry.id}:downloading`));
        if (!finished) return undefined;
        setHeld(finished);
        const timer = window.setTimeout(() => setHeld(null), 4000);
        return () => window.clearTimeout(timer);
    }, [playlists, live]);
    const entry = live || held;
    if (!entry) return null;
    const percent = onDemandPercent(entry.progress);
    return (
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 40, backgroundColor: 'rgba(13,71,161,0.92)', color: '#fff', pointerEvents: 'none' }}>
            <Box sx={{ px: 2, py: 1, fontSize: 28, fontWeight: 700, textAlign: 'center' }}>{onDemandBannerLabel(entry)}</Box>
            {entry.state === 'downloading' && (
                <Box sx={{ height: 6, backgroundColor: 'rgba(255,255,255,0.25)' }}>
                    <Box sx={{ height: '100%', width: `${percent}%`, backgroundColor: '#69f0ae' }} />
                </Box>
            )}
        </Box>
    );
};
